import React, { useState, useRef, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

/**
 * Calls the check-auth-provider Edge Function to determine whether the given
 * email belongs to a social-only account (no email/password login set up).
 *
 * Returns true  → confirmed social-only account
 * Returns false → email/password account, non-existent account, or any error
 *                 (account existence is never revealed to callers)
 *
 * Accepts an AbortSignal so the caller can cancel stale in-flight requests.
 */
const fetchIsSocialOnly = async (email, signal) => {
  if (!supabaseUrl || !email?.trim()) return false;
  try {
    const url = `${supabaseUrl}/functions/v1/check-auth-provider`;
    const res = await fetch(url, {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        // Send the anon key so the edge function can verify the request
        // originates from this project's frontend.
        ...(supabaseAnonKey ? { 'apikey': supabaseAnonKey } : {}),
      },
      body: JSON.stringify({ email: email.trim().toLowerCase() }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    return data?.isSocialOnly === true;
  } catch (err) {
    // AbortError is expected when we cancel a stale request — not an error.
    if (err?.name === 'AbortError') throw err;
    return false;
  }
};

const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // null = not yet checked / email changed, false = not social-only, true = social-only
  const [isSocialOnly, setIsSocialOnly] = useState(null);
  const [isCheckingProvider, setIsCheckingProvider] = useState(false);

  // AbortController for the current in-flight provider check
  const abortRef = useRef(null);
  // The promise for the current in-flight check (so submit can await it)
  const pendingCheckRef = useRef(null);
  // The email value that the current check was started for
  const checkedEmailRef = useRef('');

  /**
   * Cancel any in-flight provider check and clear related state.
   */
  const cancelPendingCheck = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    pendingCheckRef.current = null;
    checkedEmailRef.current = '';
  }, []);

  /**
   * Start a provider check for `emailValue`. Cancels any currently running
   * check first. Commits the result only if the checked email still matches
   * the current input when the request resolves, preventing stale overwrites.
   */
  const startProviderCheck = useCallback((emailValue) => {
    const normalised = emailValue?.trim()?.toLowerCase() ?? '';

    if (!normalised || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalised)) {
      // Invalid/empty — cancel any pending check and reset
      cancelPendingCheck();
      setIsSocialOnly(null);
      setIsCheckingProvider(false);
      return null;
    }

    // Cancel the previous request
    if (abortRef.current) abortRef.current.abort();

    const controller = new AbortController();
    abortRef.current = controller;
    checkedEmailRef.current = normalised;
    setIsCheckingProvider(true);

    const promise = fetchIsSocialOnly(normalised, controller.signal)
      .then((result) => {
        // Only commit if this request was not aborted and the email still
        // matches the current input (guards against out-of-order resolution).
        if (!controller.signal.aborted && checkedEmailRef.current === normalised) {
          setIsSocialOnly(result);
          setIsCheckingProvider(false);
        }
        return result;
      })
      .catch((err) => {
        if (err?.name !== 'AbortError') {
          // Unexpected error — fail safe
          if (!controller.signal.aborted && checkedEmailRef.current === normalised) {
            setIsSocialOnly(false);
            setIsCheckingProvider(false);
          }
        }
        // Return false for aborted/errored checks
        return false;
      });

    pendingCheckRef.current = promise;
    return promise;
  }, [cancelPendingCheck]);

  const handleEmailBlur = () => {
    const normalised = email?.trim()?.toLowerCase() ?? '';
    // Skip if we've already checked this exact email
    if (normalised && normalised === checkedEmailRef.current && isSocialOnly !== null) return;
    startProviderCheck(email);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!email?.trim()) {
      setError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      const normalised = email.trim().toLowerCase();

      // If no check has been started for this email, start one now.
      // If a check is already in-flight (user blurred then submitted quickly),
      // await it so we don't skip the warning.
      let socialOnly = isSocialOnly;
      if (isSocialOnly === null || checkedEmailRef.current !== normalised) {
        // Start a fresh check (cancels any stale one)
        const checkPromise = startProviderCheck(normalised);
        socialOnly = checkPromise ? await checkPromise : false;
      } else if (pendingCheckRef.current && checkedEmailRef.current === normalised) {
        // Check is in-flight for the right email — await it
        socialOnly = await pendingCheckRef.current;
      }

      // socialOnly is now settled. Update state so the warning renders before
      // we send the reset email (gives the user a moment to see it).
      setIsSocialOnly(socialOnly);

      const { error: resetError } = await supabase?.auth?.resetPasswordForEmail(
        normalised,
        { redirectTo: `${window.location?.origin}/reset-password` }
      );
      if (resetError) throw resetError;
      setIsSuccess(true);
    } catch (err) {
      console.error('Password reset error:', err);
      // Don't reveal whether email exists — show generic success
      setIsSuccess(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Cancel any in-flight check so stale results can't leak into the next open
    cancelPendingCheck();
    setEmail('');
    setError('');
    setIsSuccess(false);
    setIsSocialOnly(null);
    setIsCheckingProvider(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[1040] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-xl w-full max-w-md animate-slide-down">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-heading font-semibold text-foreground">
            Reset Password
          </h2>
          <button
            onClick={handleClose}
            className="p-1 rounded-md hover:bg-muted transition-smooth"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-6">
          {!isSuccess ? (
            <>
              <p className="text-sm text-muted-foreground mb-4">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              {/* Social-only warning — shown only when the server confirms the
                  account uses social login exclusively. Never shown for unknown
                  or email/password accounts, and never shown while the check is
                  still in progress. */}
              {isSocialOnly === true && (
                <div className="flex items-start gap-2 bg-muted/50 border border-border rounded-md px-3 py-2.5 mb-5">
                  <Icon
                    name="Info"
                    size={15}
                    color="var(--color-muted-foreground)"
                    className="mt-px shrink-0"
                  />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    This account was created with Google or another social provider. Sending
                    this link will{' '}
                    <span className="font-medium text-foreground">add</span> email/password
                    login to your account rather than replacing your existing sign-in method.
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label="Email Address"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => {
                    const next = e?.target?.value ?? '';
                    setEmail(next);
                    if (error) setError('');
                    // Reset provider state whenever the email changes so a
                    // stale result from a previous address is never shown.
                    if (isSocialOnly !== null || isCheckingProvider) {
                      cancelPendingCheck();
                      setIsSocialOnly(null);
                      setIsCheckingProvider(false);
                    }
                  }}
                  onBlur={handleEmailBlur}
                  error={error}
                  required
                />

                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    fullWidth
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="default"
                    loading={isLoading || isCheckingProvider}
                    fullWidth
                  >
                    Send Reset Link
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle2" size={32} color="var(--color-success)" />
              </div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                Check Your Email
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                If an account exists for{' '}
                <span className="font-medium text-foreground">{email}</span>,
                you'll receive a password reset link shortly.
              </p>
              <Button variant="default" onClick={handleClose} fullWidth>
                Got It
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
